import { JUSTIFY_CONTENT, PADDING, TEXT_ALIGN } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, createHtmlElement, getPxValue, htmlStringToText } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { Action } from "@cervval.js/common-action/src/action.model";
import { filterComparatorNumber } from "@cervval.js/common-filter/src/filterExpression.number";
import { PropertyConfig, PropertyOptions } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { getDuplicates, hasDuplicates } from "@cervval.js/common/src/tools/collection.array.tools";
import { buildObjectMap } from "@cervval.js/common/src/tools/collection.map.tools";
import { isNumber, toPx } from "@cervval.js/common/src/tools/number.tools";
import { FilterOptions } from "@cervval.js/common/src/tools/string.tools";
import { ColumnUid, SmartTableColumnConfig } from "@cervval.js/common/src/ui/columnConfiguration/columnConfiguration";
import { CollectionTools, anyReallyAny } from "@cervval.js/common/tools";
import { Tooltip } from "../../bootstrap";
import { CjsEditor } from "../../editors/editor";
import { CjsInput } from "../inputs/input";
import { CopyPasteHandler } from "./copyPaste/smartTableCopyPaste.types";
import { CjsSmartTable, RenderCacheData, SmartTableCell, SmartTableCellRenderer, SmartTableCellStyleGetter, SmartTableValueEditOrigin } from "./smartTable";

/** Css classes */
export const CSS_CLASS_RESIZABLE_COLUMN_HEADER = "col-header-resizable";
export const CSS_CLASS_COLUMN_RESIZING = "col-resizing";

/** Constants */
/** "@selectorColumn" is used by SmartTableCheckboxSelectorColumn (placed here to avoid circular dependency) */
export const SELECTOR_COLUMN_UID = "@selectorColumn";

/** Type of SmartTableCell in getCellValue callback (Omit html, stringValue and value as they are not computed yet when calling getCellValue) */
export type SmartTableCellGetValue<ItemData> = Omit<SmartTableCell<ItemData>, "html" | "stringValue" | "value">;
/** Type of SmartTableCell in getDisplayString callback (Omit stringValue as it is built when calling getDisplayString)*/
export type SmartTableCellGetDisplayString<ItemData> = Omit<SmartTableCell<ItemData>, "stringValue">;

/**
 * Column of SmartTable
 * A column can be define by a columnUid/label or a PropertyConfig
 * A cellStyle can by apply to column
 */
export interface SmartTableColumn<ItemData> extends FilterOptions<ItemData> {

    /** 
     * Uid of the column (usually it is the property name but we could have columns without a property config or have two columns with the same property name) 
     * SmartTable internal columns (such as selection column) are prefixed with @ to avoid conflict with application column uids
     */
    uid: ColumnUid;

    /** If true, column stays at left:0 when scrolling (using css `position: sticky`) */
    sticky?: boolean;

    /** 
     * Title is added to table header TH element
     * If no title is defined, we use the propertyConfig.label
     * * when using the function, you don't need to append the titleElement to thElement, it will be done by the table (that also handles sort and filter)
     * th is provided to the function only to be able to add classes or style the element
     */
    title?: AcceptedHtmlElementContent | ((thElement: HTMLTableCellElement) => AcceptedHtmlElementContent | undefined);

    /**
     * Title custom tooltip
     * Will replace default tooltip of column title
     */
    titleTooltip?: Partial<Tooltip.Options> | string;

    /** 
     * Footer is added to table header TH element
     * If no footer is defined, we use the propertyConfig.label
     * * when using the function, you don't need to append the footerElement to thElement, it will be done by the table (that also handles sort and filter)
     * th is provided to the function only to be able to add classes or style the element
     */
    footer?: AcceptedHtmlElementContent | ((thElement: HTMLTableCellElement) => AcceptedHtmlElementContent | undefined);

    /**
     * This function is called before render start
     */
    prepareForRender?: (renderCacheData: RenderCacheData) => void;

    /**
     * This function is called on render finish
     */
    finalizeRender?: (renderCacheData: RenderCacheData) => void;

    /**
     * This function is called after table.refreshRowsSelectionDisplay
     */
    refreshSelectionDisplay?: () => void;

    /** 
     * If defined, overrides getCellValue for the cells of the column  
     * a new renderCacheData is created on each table render, it can be used by getCellValue to store temporary data shared by cells during a render
     * if getCellValue is not called as part of the table render, renderCacheData may be undefined
     */
    getCellValue?: (cell: SmartTableCellGetValue<ItemData>, renderCacheData?: RenderCacheData) => anyReallyAny;

    /**
     * If defined, overrides setCellValue (SmartTableEditionHelper) for the cells of the column  
     * Sets new value of cell within row data (the cell may not be rendered in the DOM and won't be rendered by this function)
     * If you override setCellValue, cellEditedValueEvent is not emitted, you must emit it in this function if needed
     */
    setCellValue?(cell: SmartTableCell<ItemData>, newValue: anyReallyAny, origin: SmartTableValueEditOrigin, action?: Action): Promise<void>;

    /** If defined, overrides renderCell for the cells of the column */
    renderCell?: SmartTableCellRenderer<ItemData>;

    /** If defined, overrides getCellStyle for the cells of the column */
    getCellStyle?: SmartTableCellStyleGetter<ItemData>;

    /**
     * Classes added to cell div that surrounds the renderCell div (ie the first child inside td)
     */
    cellCssClasses?: string[];

    /**
     * Classes added to the td that contain the cell
     */
    tdCssClasses?: string[];

    /**
     * Columns alignment
     * Will align header text and cell content
     * 
     * It is equivalent to adding classes to cell-content div
     * If renderCell returns a htmlElement: JUSTIFY_CONTENT classes are added to cell-content
     * If renderCell returns a string: TEXT-START/END classes are added to text span
     * left => JUSTIFY_CONTENT.START or TEXT-START
     * right => JUSTIFY_CONTENT.END or TEXT-END
     * @default left
     */
    horizontalAlign?: SmartTableColumnAlign;

    /** If defined, overrides getCellEditor for the cells of the column */
    getCellEditor?: (cell: SmartTableCell<ItemData>) => CjsEditor<anyReallyAny> | undefined;

    /** Define stringify callback for cell value, will be used by filter or excel export if defined */
    getDisplayString?: (cell: SmartTableCellGetDisplayString<ItemData>, renderCacheData?: RenderCacheData) => string;

    /** most of the time the column is linked to a PropertyConfig */
    propertyConfig?: PropertyConfig;

    /** Order of column in table (undefined === not ordered) */
    order?: number;

    /** Comparator used to sort on column, If undefined use compare function in object.tools */
    sortComparator?: (item1: ItemData | null, value1: anyReallyAny, item2: ItemData | null, value2: anyReallyAny) => number;

    /** If true, there won't be any sort functionality in the column header  */
    sortDisabled?: boolean;

    /** If true, there won't be any select callback in the column */
    selectCallbackDisabled?: boolean;

    /** HTML element of SmartTableColumn, if rendered in DOM */
    headerHtml?: HTMLElement;

    /** HTML element of SmartTableColumn, if rendered in DOM */
    footerHtml?: HTMLElement;

    /** 
     * Set with of column
     * @type width accepts css values(px, em, rem, %, vw, vh)
     * @description only use this option to set width of column, current width of column isn't store in this variable
     * 
     * !For percentages table display should be "fixed"(options.columnsLayout === "fixed")
     */
    width?: string;

    /**
     * If true, we won't be able to resize a column with the mouse
     * ! Resizable column won't work properly if 'columnsLayout' option is "auto", use "fixed" !
     */
    resizeDisabled?: boolean;

    /**
     * CjsInput of column used to filter values
     * ! Not defined if header is disabled or filter is disabled on column
     */
    filterInput?: CjsInput<string>;

    /**
     * Boolean to indicate if we want the advanced filter to separate levels of hierarchy for this column
     */
    advancedFilterWithDepth?: boolean;

    /**
     * Callback to generate text for depth filter
     * Allow you to override the default text generated by those filter
     * Will not use the depth if there is a duplicate value that are coming from the callback
     */
    textForAdvancedFilterWithDepth?: (pc: PropertyConfig, depth: number) => string;

    /**
     * List of handlers for copy/paste with SmartTableEditionHelper.    
     * Copy with all handlers.      
     * Pastes with the first handler that successfully converts the data to its type in ascending order.     
     * If not defined, use {@link DEFAULT_COPY_PAST_HANDLERS} 
     */
    copyPasteHandlers?: CopyPasteHandler<ItemData>[];
}

/** Alignment of SmartTable column */
export type SmartTableColumnAlign = "right" | "left" | "center";

/**
 * @returns getVisibleColumnsFromConfiguration().length + 1 if addFillColumn
 */
export function getVisibleColumnsCount<ItemData>(columns: SmartTableColumn<ItemData>[], columnsConfig: SmartTableColumnConfig[] | undefined, addFillColumn: boolean | undefined): number {
    const cols = getVisibleColumnsFromConfiguration(columns, columnsConfig);
    return cols.length + (addFillColumn ? 1 : 0);
}

/** Get columns from columns configuration */
export function getVisibleColumnsFromConfiguration<ItemData>(columns: SmartTableColumn<ItemData>[], columnsConfig: SmartTableColumnConfig[] | undefined): SmartTableColumn<ItemData>[] {
    if (!columnsConfig) {
        return columns;
    }

    const columnsFromConfig: SmartTableColumn<ItemData>[] = [];
    const columnsByUids = buildObjectMap(columns, "uid");
    for (const columnConfig of columnsConfig) {
        if (!columnConfig.visible) { continue; }

        const column = columnsByUids.get(columnConfig.uid);
        if (!column) { continue; }

        columnsFromConfig.push(column);
    }
    return columnsFromConfig;

}

/** 
 * Get visible and non visible columns from columns configuration 
 * ! Updates the columnsConfig by adding missing columns
 * ! By default, columns that are in the columnsConfig and not in columns are not displayed and added to nonVisible array, set showMissingColumns to true if you want to force the display of all columns
 */
export function getColumnsFromConfiguration<ItemData>(columns: SmartTableColumn<ItemData>[], columnsConfig: SmartTableColumnConfig[] | undefined, showMissingColumns = false): { visible: SmartTableColumn<ItemData>[], nonVisible: SmartTableColumn<ItemData>[] } {
    if (!columnsConfig || columnsConfig.length === 0) {
        return { visible: columns, nonVisible: [] };
    }

    // read config (and keep config order)
    const columnsByUid = buildObjectMap(columns, "uid");
    const visibleColumnsFromConfig: SmartTableColumn<ItemData>[] = [];
    const nonVisibleColumnsFromConfig: SmartTableColumn<ItemData>[] = [];
    const seenUids = new Set<string>();
    let maxOrder = 0;
    for (const columnConfig of columnsConfig) {
        const column = columnsByUid.get(columnConfig.uid);
        if (column == null) {
            // column does not exist anymore
            continue;
        }
        maxOrder = Math.max(maxOrder, columnConfig.order);
        if (columnConfig.visible) {
            visibleColumnsFromConfig.push(column);
        } else {
            nonVisibleColumnsFromConfig.push(column);
        }
        seenUids.add(columnConfig.uid);
    }
    // add other columns
    for (const column of columns) {
        if (!seenUids.has(column.uid)) {
            seenUids.add(column.uid);
            if (showMissingColumns) { visibleColumnsFromConfig.push(column); }
            else { nonVisibleColumnsFromConfig.push(column); }
            // add to config
            columnsConfig.push({
                order: ++maxOrder,
                uid: column.uid,
                visible: showMissingColumns
            });
        }
    }
    return { visible: visibleColumnsFromConfig, nonVisible: nonVisibleColumnsFromConfig };
}

/** 
 * Get first column 
 * search in columnsConfig if set
 */
export function getFirstColumn<ItemData>(columns: SmartTableColumn<ItemData>[], columnsConfig: SmartTableColumnConfig[] | undefined, options?: { excludeCheckboxColumn?: boolean }): SmartTableColumn<ItemData> | undefined {
    if (!columnsConfig && columns.length) {
        return options?.excludeCheckboxColumn ? columns.find(c => !isCheckboxColumn(c.uid)) : columns[0];
    }

    const columnConfig = columnsConfig?.find(c => (options?.excludeCheckboxColumn ? !isCheckboxColumn(c.uid) : c.order === 1));
    if (!columnConfig) { return undefined; }

    return columns.find(c => c.uid === columnConfig.uid);
}

/** Build initial column configuration */
export function buildInitialColumnConfiguration<ItemData>(columns: SmartTableColumn<ItemData>[], defaultVisibleColumns?: string[]): SmartTableColumnConfig[] {
    const defaultVisibility = defaultVisibleColumns == null ? true : false;// if no defaultVisibleColumns they are all visible
    const minIndex = defaultVisibleColumns == null ? 0 : defaultVisibleColumns.length;
    return columns.map((col, i) => {
        const defaultVisibleColumnsIndex = defaultVisibleColumns?.indexOf(col.uid) ?? -1;
        return {
            uid: col.uid,
            visible: defaultVisibleColumnsIndex >= 0 ? true : defaultVisibility,
            order: defaultVisibleColumnsIndex >= 0 ? defaultVisibleColumnsIndex : minIndex + i + 1
        };
    });
}

/** Set a non visible column visible in colum configuration for this columnUid, reset the orders 
 * @param columnUids columns to be set visible/non visible
 * @param columnsConfig original column configuration
 * @returns new column configuration with column matching columnUid at the last order of visible / non visible list
 */
export function updateVisibleColumnConfigurations(columnUids: ColumnUid[], columnsConfig: SmartTableColumnConfig[]): boolean {
    // partition visible and non visible columns
    const visible: SmartTableColumnConfig[] = [];
    const nonVisible: SmartTableColumnConfig[] = [];
    const toMove: SmartTableColumnConfig[] = [];
    let setVisible = true;
    for (const col of columnsConfig) {
        if (CollectionTools.includes(columnUids, col.uid)) {
            toMove.push(col);
            col.visible = !col.visible;
            setVisible = col.visible;
        } else if (!col.visible) {
            nonVisible.push(col);
        } else {
            visible.push(col);
        }
    }

    if (toMove.length > 0) {
        // Rebuild the order
        let order = 1;
        for (const cc of visible) {
            cc.order = order++;
        }
        if (setVisible) { // put the new visibles to the end of visible list
            for (const cc of toMove) {
                cc.order = order++;
            }
        }
        for (const cc of nonVisible) {
            cc.order = order++;
        }
        if (!setVisible) { // put the new nonVisibles to the end of non visible list
            for (const cc of toMove) {
                cc.order = order++;
            }
        }
        columnsConfig.sort((c1, c2) => c1.order - c2.order);
        return true;
    } else {
        return false;
    }
}

/** Update order in colum configuration for these columnUids
 * Columns are all moved to the same spot just above or below the selection 
 * @param columnUids columns to be moved up or down
 */
export function updateOrderColumnConfiguration(columnUids: ColumnUid[], columnsConfig: SmartTableColumnConfig[], move: { up: boolean, allTheWay: boolean }): boolean {
    let visibleBefore: SmartTableColumnConfig[] = [];
    let visibleAfter: SmartTableColumnConfig[] = [];
    const toMove: SmartTableColumnConfig[] = [];
    const nonVisible: SmartTableColumnConfig[] = [];

    // Partition columns in before, after and non visible
    for (const col of [...columnsConfig].sort((c1, c2) => c1.order - c2.order)) {
        if (!col.visible) {
            nonVisible.push(col);
        } else if (CollectionTools.includes(columnUids, col.uid)) {
            toMove.push(col);
        } else if (move.up && toMove.length === 0) {
            visibleBefore.push(col);
        } else if (move.up && toMove.length !== 0) {
            visibleAfter.push(col);
        } else if (!move.up && toMove.length === columnUids.length) {
            visibleAfter.push(col);
        } else if (!move.up && toMove.length !== columnUids.length) {
            visibleBefore.push(col);
        }
    }

    if (move.up && visibleBefore.length) {
        if (move.allTheWay) {
            // Put on the top of the list, place before list in after list
            visibleAfter = visibleBefore.concat(visibleAfter);
            visibleBefore = [];
        } else {
            // Remove last column before to place it in the after list
            const lastVisible = visibleBefore[visibleBefore.length - 1];
            visibleAfter = [lastVisible].concat(visibleAfter);
            visibleBefore = visibleBefore.slice(0, visibleBefore.length - 1);
        }
    } else if (!move.up && visibleAfter.length) {
        if (move.allTheWay) {
            // Put at the bottom of the list, place after list in before list
            visibleBefore = visibleBefore.concat(visibleAfter);
            visibleAfter = [];
        } else {
            // Remove first column after to place it in the before list
            const firstVisible = visibleAfter[0];
            visibleBefore = visibleBefore.concat([firstVisible]);
            visibleAfter = visibleAfter.slice(1);
        }
    }

    if (toMove.length !== columnUids.length) { return false; }
    // Rebuild the order
    const orderedList = visibleBefore.concat(toMove, visibleAfter, nonVisible);
    let order = 1;
    for (const columnConfig of orderedList) {
        const originalColumn = columnsConfig.find(c => c.uid === columnConfig.uid);
        if (originalColumn) {
            originalColumn.order = order;
        }
        order++;
    }
    return true;

}

/**
 * @returns a SmartTableColumn with value from params.p or params.getValue
 */
export function pushSmartTableColumn<ItemData>(list: SmartTableColumn<ItemData>[], params: {
    /** property name */
    p?: keyof ItemData,
    /** uid is used to get i18n title, by default it is also the column uid, it can be overidden in options if i18n is not aligned on column uids */
    uid?: string,
    getValue?: (rowItemData: ItemData, renderCacheData: RenderCacheData | undefined) => unknown,
    i18n?: Record<string, string>
}, options?: Partial<SmartTableColumn<ItemData>>
): SmartTableColumn<ItemData> {

    const uid = String(params?.uid ?? params.p ?? (list.length + 1));
    const col: SmartTableColumn<ItemData> = {
        uid,
        getCellValue: (cell, renderCacheData) => {
            if (cell.rowItemData == null) { return null; }
            if (params?.getValue) {
                return params.getValue(cell.rowItemData, renderCacheData);
            }
            if (params.p) {
                return cell.rowItemData[params.p];
            }
            return null;
        },
        title: params?.i18n?.[uid],
        ...options
    };
    list.push(col);
    return col;
}

/**
 * @returns a SmartTableColumn corresponding to the PropertyConfig
 */
export function smartTablePropertyColumn<ItemData>(propertyConfig: PropertyConfig, options?: Partial<SmartTableColumn<ItemData>>): SmartTableColumn<ItemData> {
    const col: SmartTableColumn<ItemData> = {
        uid: propertyConfig.propertyName,
        propertyConfig,
        ...options
    };
    return col;
}

/** 
 * Generates a list of SmartTable columns from the given property configuration list.
 * 
 * @param propertyConfigList The original list of property config to be converted to columns.
 * @param options List of options to tweak the generation
 * @param options.ignoreProperties If specified, any property config with propertyName in the array will be ignored.
 * @param options.genericOptions Standard options that will be applied to the column if no specific options is defined customOptions.
 * @param options.customOptions Allows to override some options for a specific propertyName. Values here are merged with the one in genericOptions.
 */
export function getSmartTablePropertyColumnsFromPropertyConfigs<ItemData = unknown>(propertyConfigList: PropertyConfig[], options?: {
    ignoreProperties?: (keyof ItemData)[],
    genericOptions?: Partial<SmartTableColumn<ItemData>>,
    customOptions?: { [propertyName: string]: Partial<SmartTableColumn<ItemData>> }
}): SmartTableColumn<ItemData>[] {
    const result: SmartTableColumn<ItemData>[] = [];

    for (const pc of propertyConfigList) {
        if (options?.ignoreProperties?.includes(pc.propertyName as keyof ItemData)) { // Here we need to trust the developer to have used ItemData keys
            // Property must be ignored
            continue;
        }

        let columnOptions: Partial<SmartTableColumn<ItemData>> = {
            ...options?.genericOptions,
            filterComparator: pc.typeUid === PropertyConfigType.NUMBER ? filterComparatorNumber : undefined
        };
        columnOptions = {
            ...columnOptions,
            ...options?.customOptions?.[pc.propertyName]
        };

        result.push(smartTablePropertyColumn(pc, columnOptions));
    }
    return result;
}

/**
 * Finds the column and set SmartTableColumn attributes
 */
export function setColumnAttributes<ItemData>(columns: SmartTableColumn<ItemData>[], colUid: string, attributes: Partial<SmartTableColumn<ItemData>>): void {
    const col = columns.find(c => c.uid === colUid);
    if (col == null) {
        getLogConsole().error("Column " + colUid + " was not found");
        return;
    }
    for (const a in attributes) {
        setProp(col, a, getProp(attributes, a));
    }
}


/** Convert column width to string */
export function columnWidthToString(width: string | number): string {
    return isNumber(width) ? toPx(width) : width;
}

/**
 * Returns width of the column based on the column config or on the column if no column config
 * @param smartTable
 * @param col Column to get the width
 * @param columnsConfig Original columns configuration
 */
export function getSmartTableColumnWidth<ItemData>(smartTable: CjsSmartTable<ItemData>, col: SmartTableColumn<ItemData>, columnsConfig: SmartTableColumnConfig[] | undefined): string | undefined {
    // Retrieve column config for current col
    const columnConfig = columnsConfig?.find(colConfig => colConfig.uid === col.uid);
    const colWidth = columnConfig?.width ?? col.width;
    if (!colWidth) { return undefined; }

    const colWidthPxValue = getPxValue(colWidth, { parentElement: smartTable, parentWidth: smartTable.lastRenderBounds?.table.width });
    if (colWidthPxValue === undefined) { return undefined; }
    return colWidthPxValue > smartTable.options.minColumnResizeWidth ? colWidth : toPx(smartTable.options.minColumnResizeWidth);
}

/**
 * @returns true if a given column is resizable
 * @param smartTable of column
 * @param col Column to get the width
 */
export function isSmartTableColumnResizable<ItemData>(smartTable: CjsSmartTable<ItemData>, col: SmartTableColumn<ItemData>): boolean {
    return !Boolean(smartTable.options.columnsResizeDisabled) && !col.resizeDisabled;
}

/**
 * Set the width of the col header element
 * @param smartTable
 * @param col Column to get the width
 * @param columnsConfig Original columns configuration
 */
export function setSmartTableColumnHeaderWidthHTML<ItemData>(smartTable: CjsSmartTable<ItemData>, col: SmartTableColumn<ItemData>, columnsConfig: SmartTableColumnConfig[] | undefined): void {
    // Return if header html doesn't exist because we want to set its width
    if (!col.headerHtml) { return; }

    // First get columnConfig width, set get col width if column config width doesn't exist 
    const widthToSet = getSmartTableColumnWidth(smartTable, col, columnsConfig);

    // Set width to the html element
    if (widthToSet != null) {
        col.headerHtml.style.width = widthToSet;
    }
}

/**
 * Returns a record of columns width by columns uids
 * @param smartTable
 * @param columns Columns to get the width
 * @param columnsConfig Original columns configuration
 */
export function getSmartTableColumnsWidthByUid<ItemData>(smartTable: CjsSmartTable<ItemData>, columns: SmartTableColumn<ItemData>[], columnsConfig: SmartTableColumnConfig[] | undefined): Record<string, string> {
    const columnWidthByUid: Record<string, string> = {};

    for (const col of getVisibleColumnsFromConfiguration<ItemData>(columns, columnsConfig)) {
        const colWidth = getSmartTableColumnWidth(smartTable, col, columnsConfig);
        if (colWidth) {
            columnWidthByUid[col.uid] = colWidth;
        }
    }

    return columnWidthByUid;
}

/**
 * Create resizer div which will be used for the resize interaction
 * @param smartTable SmartTable
 * @param column Column which resizer will be added on
 * @returns HTMLDivElement
 */
export function createSmartTableResizerColumn<ItemData>(smartTable: CjsSmartTable<ItemData>, column: SmartTableColumn<ItemData>): HTMLDivElement {
    const resizer = createHtmlElement("div", { cssClass: CSS_CLASS_RESIZABLE_COLUMN_HEADER });

    // Add a resizer element to the column
    column.headerHtml?.append(resizer);

    // Set the column resizable
    setSmartTableResizableColumn(smartTable, column, resizer);

    return resizer;
}

/**
 * Install all listeners to make a column resizable
 * @param smartTable SmartTable
 * @param column Column to set rsizable
 * @param resizerDiv Resizer div used for users interaction
 */
export function setSmartTableResizableColumn<ItemData>(smartTable: CjsSmartTable<ItemData>, column: SmartTableColumn<ItemData>, resizerDiv: HTMLDivElement): void {
    // Track the current position of mouse
    let mouseDownPositionX = 0;
    let offsetX = 0;
    let widthBeforeResize = 0;
    let widthAfterResize = 0;

    const mouseDownHandler = (e: MouseEvent): void => {
        if (!column.headerHtml) { return; }

        // Get the current mouse position
        mouseDownPositionX = e.clientX;

        // Calculate the current width of column
        const styles = window.getComputedStyle(column.headerHtml);
        const pxValue = getPxValue(styles.width, { parentElement: smartTable, parentWidth: smartTable.lastRenderBounds?.table.width });
        if (pxValue === undefined) { return; }
        widthBeforeResize = pxValue;

        // Attach listeners for tableHeader's events
        document.addEventListener("mousemove", mouseMoveHandler);
        document.addEventListener("mouseup", mouseUpHandler);

        resizerDiv.classList.add(CSS_CLASS_COLUMN_RESIZING);

        // Emit event
        smartTable.startColResizeEvent.emit({ column, columnWidth: widthBeforeResize, offsetX });
    };

    const mouseMoveHandler = (e: MouseEvent): void => {
        if (!column.headerHtml) { return; }

        // Determine how far the mouse has been moved
        offsetX = e.clientX - mouseDownPositionX;

        // Update the width of column
        const newWidthAfterResize = widthBeforeResize + offsetX;

        // Check for min width of column, don't resize if widthAfterResize is lower than minColumnResizeWidth
        if (newWidthAfterResize < smartTable.options.minColumnResizeWidth) { return; }

        // Check if new column width is valid (available space in)
        const isColumnNewWidthValid = checkColumnsWidth(smartTable, smartTable.options.columns, { newColumn: column, newWidth: newWidthAfterResize });
        if (!isColumnNewWidthValid) { return; }

        // Assign new width
        widthAfterResize = newWidthAfterResize;
        const bodyWidth = smartTable.components.tableBody.clientWidth;

        let widthLeftForOtherColumns = 0;
        // Compute total width of others columns
        for (const otherColumn of getVisibleColumnsFromConfiguration(smartTable.options.columns, smartTable.options.columnsConfig)) {
            if (otherColumn.uid === column.uid) { continue; }// Don't handle column in resize
            const colWidth = getSmartTableColumnWidth(smartTable, otherColumn, smartTable.options.columnsConfig);
            if (colWidth == null) { continue; }

            // Get column width value in px
            const colWithPxValue = getPxValue(colWidth, { parentElement: smartTable, parentWidth: smartTable.lastRenderBounds?.table.width });
            if (colWithPxValue == null) { continue; }

            let newColWidth = smartTable.options.minColumnResizeWidth;
            // Only assign column width if value is higher than min-width
            if (colWithPxValue > newColWidth) { newColWidth = colWithPxValue; }

            // Increment total
            widthLeftForOtherColumns += newColWidth;
        }

        // If other columns total width plus new column width is higher than smartTable width, don't resize
        // If horizontal scroll isn't enable, we check total columns width to ensure columns don't disappear with resize
        if (!smartTable.options.enableHorizontalScroll && bodyWidth - widthLeftForOtherColumns - widthAfterResize < 0) {
            return;
        }
        // Set style width
        column.headerHtml.style.width = toPx(widthAfterResize);

        // Emit event
        smartTable.resizingColEvent.emit({ column, columnWidth: widthAfterResize, offsetX });
    };

    // When user releases the mouse, remove the existing event listeners
    const mouseUpHandler = (): void => {
        document.removeEventListener("mousemove", mouseMoveHandler);
        document.removeEventListener("mouseup", mouseUpHandler);
        resizerDiv.classList.remove(CSS_CLASS_COLUMN_RESIZING);

        const isColumnNewWidthValid = checkColumnsWidth(smartTable, smartTable.options.columns, { newColumn: column, newWidth: widthAfterResize });
        if (!isColumnNewWidthValid) { return; }

        // Set width into smart table column object only after resize
        smartTable.styleHelper.setColumnWidth(column.uid, toPx(widthAfterResize));

        // Emit event
        smartTable.endColResizeEvent.emit({ column, columnWidth: widthAfterResize, offsetX });
    };

    const doubleClickHandler = (): void => {
        if (!column.headerHtml) { return; }
        const isAutoLayout = smartTable.options.columnsLayout === "auto";
        if (isAutoLayout) { return; }// Table is already in auto-fit behavior
        // Reset value of column width
        smartTable.styleHelper.setColumnWidth(column.uid, "0px");

        // ? AMT: It's a little "trick" to process an "autofit" behavior, I didn't find a better one...
        // Set to column layout auto, to generate an autoFit
        smartTable.options.setAndRefreshIfChanged({ columnsLayout: "auto" });
        // Set width into smart table column object only after resize
        smartTable.styleHelper.setColumnWidth(column.uid, toPx(column.headerHtml.clientWidth));
        // Emit event
        smartTable.endColResizeEvent.emit({ column, columnWidth: widthAfterResize, offsetX });
        // Restore table fixed layout
        smartTable.options.setAndRefreshIfChanged({ columnsLayout: "fixed" });
    };

    resizerDiv.addEventListener("mousedown", mouseDownHandler);
    resizerDiv.addEventListener("dblclick", doubleClickHandler);
}

/** Add sticky column class to an element, handle multiple sticky columns */
export function addStickyColumn<ItemData>(smartTable: CjsSmartTable<ItemData>, column: SmartTableColumn<ItemData>, elt: HTMLElement): void {
    // Add sticky class
    elt.classList.add("sticky-column");

    // Sticky columns width cannot be applied if layout isn't fixed
    if (smartTable.options.columnsLayout !== "fixed") {
        getLogConsole().warn("Sticky columns width cannot be applied if layout of table isn't 'fixed'");
        return;
    }

    // Get columns ordered
    const columns = getVisibleColumnsFromConfiguration(smartTable.options.columns, smartTable.options.columnsConfig);

    // List all previous sticky columns
    const previousStickyColumns: SmartTableColumn<ItemData>[] = [];
    for (const col of columns) {
        // Useless to continue, we reach current column
        if (col.uid === column.uid) { break; }

        // If col isn't sticky, continue
        if (!col.sticky) { continue; }

        previousStickyColumns.push(col);
    }

    // set left so that element don't overlap previous sticky columns
    const previousStickyColumnsTotalWidth = previousStickyColumns.reduce((acc, currentCol) => {
        return acc + (currentCol.width ? parseInt(currentCol.width, 10) : 0);
    }, 0);
    elt.style.left = toPx(previousStickyColumnsTotalWidth);
}

/**
 * SmartTableColumn with properties defined bu no function implemented
 */
export class AbstractSmartTableColumn<ItemData> implements SmartTableColumn<ItemData> {

    public title?: AcceptedHtmlElementContent | ((thElement: HTMLTableCellElement) => AcceptedHtmlElementContent | undefined) | undefined;
    public footer?: AcceptedHtmlElementContent | ((thElement: HTMLTableCellElement) => AcceptedHtmlElementContent | undefined) | undefined;
    public propertyConfig?: PropertyConfig<PropertyConfigType, PropertyOptions> | undefined;
    public order?: number | undefined;
    public filterDisabled?: boolean | undefined;
    public sortDisabled?: boolean | undefined;
    public resizeDisabled?: boolean | undefined;

    public headerHtml?: HTMLElement | undefined;
    public footerHtml?: HTMLElement | undefined;
    public width?: string;
    public uid: string;

    public titleTooltip?: Partial<Tooltip.Options> | string;

    constructor(uid: string) {
        this.uid = uid;
    }

}

/** @returns true if list of columns has some uid duplicates */
export function checkColumnDuplicatesUid<ItemData>(columns: SmartTableColumn<ItemData>[], options?: { displayLogError?: boolean }): boolean {
    if (!columns.length) { return false; }
    const columnUids = columns.map(col => col.uid);


    const duplicates = hasDuplicates(columnUids);
    if (duplicates && options?.displayLogError) {
        getLogConsole().warn(`[SmartTable] - List of given columns has uid duplicates: ${getDuplicates(columnUids).join(", ")}`);
    }

    return duplicates;
}

/** 
 * Check all visible columns width
 * if total of columns width is upper than available width, impossible to resize column, no space available
 * @param smartTable target
 * @param columns to check
 * @param p optional params
 * @param p.newColumn Check columns width of given column (used during a column resize)
 * @param p.newWidth Check columns width with new column width value (used during a column resize)
 * @returns true if columns are resizable
 */
export function checkColumnsWidth<ItemData>(smartTable: CjsSmartTable<ItemData>, columns: SmartTableColumn<ItemData>[], p?: { newColumn: SmartTableColumn<ItemData>, newWidth: number }): boolean {

    // No width check for auto layout
    if (smartTable.options.columnsLayout === "auto") { return true; }
    // Horizontal scroll => no width constrained => no width check
    if (smartTable.options.enableHorizontalScroll) { return true; }

    // Store columns computed width value
    const columnWidthByUid: Record<ColumnUid, {
        /** Width in config */
        width: string | undefined,
        /** Current width computed */
        widthValue: number
    }> = {};

    // Split between fixed and non-fixed width columns
    const nonFixedWidthColumns: SmartTableColumn<ItemData>[] = [];
    const fixedWidthColumns: SmartTableColumn<ItemData>[] = [];
    for (const col of getVisibleColumnsFromConfiguration(columns, smartTable.options.columnsConfig)) {
        if (col.width) { fixedWidthColumns.push(col); }
        else { nonFixedWidthColumns.push(col); }
    }

    // Columns min width
    const columnMinWidth = smartTable.options.minColumnResizeWidth;

    // Retrieve bounds of smartTable
    const smartTableBounds = smartTable.getTableBounds();

    // Total min-width of non-fixed width columns, theses columns are auto width 
    const nonFixedColumnsTotalMinWidth = nonFixedWidthColumns.length * columnMinWidth;
    // Compute total available width without nonFixedColumns
    const availableWidth = smartTableBounds.table.width - nonFixedColumnsTotalMinWidth;

    let fixedColumnsTotalWidth = 0;
    for (const col of fixedWidthColumns) {
        if (!col.width) { continue; } // Only type checking, should never append
        if (col.uid === p?.newColumn.uid) { continue; } // Don't loop into new columnWidth value if given, compute width later
        // Retrieve width in px of column
        const colWidthPxValue = getPxValue(col.width, { parentElement: smartTable, parentWidth: smartTableBounds.table.width });
        if (colWidthPxValue == null) { continue; }
        // Increment total width
        fixedColumnsTotalWidth += colWidthPxValue;
        // Store result
        columnWidthByUid[col.uid] = { width: col.width, widthValue: colWidthPxValue };
    }

    // If new column is given, add width to total width
    const newCol = p?.newColumn;
    if (newCol) {
        columnWidthByUid[newCol.uid] = {
            width: newCol.width,
            widthValue: p.newWidth ?? (newCol.width ? getPxValue(newCol.width, { parentElement: smartTable, parentWidth: smartTableBounds.table.width }) ?? 0 : 0)
        };
        fixedColumnsTotalWidth += columnWidthByUid[newCol.uid]?.widthValue ?? 0;
    }

    // Check if width of total columns is lower than available width
    return availableWidth - fixedColumnsTotalWidth >= 0;
}

/** @returns true if column is the cjs checkbox column */
export function isCheckboxColumn(columnUid: ColumnUid): boolean {
    return columnUid === SELECTOR_COLUMN_UID;
}

/**
 * Get alignment for a given column
 * If propertyConfig is defined, alignment is determined by typeUid
 * @default left
 */
// eslint-disable-next-line complexity
export function getSmartTableColumnAlignment<ItemData>(column: SmartTableColumn<ItemData>): SmartTableColumnAlign {
    let alignment: SmartTableColumnAlign = "left";// Default

    if (column.horizontalAlign) {// Prioritize column option horizontalAlign
        alignment = column.horizontalAlign;
    } else if (column.propertyConfig) {// Else use propertyConfig for alignment
        switch (column.propertyConfig.typeUid) {
            case PropertyConfigType.ID:
            case PropertyConfigType.RANGE:
            case PropertyConfigType.NUMBER:
            case PropertyConfigType.AGE:
                alignment = "right";
                break;
            case PropertyConfigType.DATE:
            case PropertyConfigType.FINISH_DATE:
            case PropertyConfigType.CHECKBOX:
            case PropertyConfigType.CHECKBOX_GROUP:
            case PropertyConfigType.SWITCH:
            case PropertyConfigType.DURATION:
            case PropertyConfigType.DURATION_IN_SECONDS:
            case PropertyConfigType.DATETIME:
            case PropertyConfigType.TIME:
            case PropertyConfigType.TIME_IN_SECONDS:
            case PropertyConfigType.DATE_INTERVAL:
            case PropertyConfigType.PHONE_NUMBER:
            case PropertyConfigType.COLOR:
                alignment = "center";
                break;
            case PropertyConfigType.STRING:
            case PropertyConfigType.UID:
            case PropertyConfigType.TEXTAREA:
            case PropertyConfigType.COORDINATE:
            case PropertyConfigType.SEARCH:
            case PropertyConfigType.IMAGE:
            case PropertyConfigType.EDITABLE_TEXT:
            case PropertyConfigType.FILELIST:
            case PropertyConfigType.RICH_TEXT:
            case PropertyConfigType.LISTSELECT:
            case PropertyConfigType.REFLIST:
            case PropertyConfigType.REFTABLE:
            default:
                alignment = "left";
        }
    }


    return alignment;
}

/**
 * Get class for smartTable column alignment
 * @param column to retrieve alignment class
 * @param isHtmlContent if true, class will be flex alignment (we cannot determine cell content, use flex because TEXT_ALIGN won't work)
 * @returns TEXT_ALIGN or JUSTIFY_CONTENT class
 */
export function getSmartTableColumnAlignmentClass<ItemData>(column: SmartTableColumn<ItemData>, isHtmlContent?: boolean): JUSTIFY_CONTENT | TEXT_ALIGN {
    const alignment = getSmartTableColumnAlignment(column);

    let alignmentClass: JUSTIFY_CONTENT | TEXT_ALIGN;

    if (isHtmlContent) {// If html content, use flex justify-content
        if (alignment === "left") {
            alignmentClass = JUSTIFY_CONTENT.START;
        } else if (alignment === "center") {
            alignmentClass = JUSTIFY_CONTENT.CENTER;
        } else {// Right
            alignmentClass = JUSTIFY_CONTENT.END;
        }
    } else {// If string content, use text-align
        if (alignment === "left") {
            alignmentClass = TEXT_ALIGN.START;
        } else if (alignment === "center") {
            alignmentClass = TEXT_ALIGN.CENTER;
        } else {// Right
            alignmentClass = TEXT_ALIGN.END;
        }
    }

    return alignmentClass;
}


/**
 * Get padding class for smartTable column alignment
 * @param column to retrieve padding alignment class
 * @returns PADDING class
 */
export function getSmartTableColumnAlignmentPaddingClass<ItemData>(column: SmartTableColumn<ItemData>, smartTable: CjsSmartTable<ItemData>): PADDING {
    const alignment = getSmartTableColumnAlignment(column);

    let paddingClass: PADDING;
    if (alignment === "left") {
        paddingClass = PADDING.PS_1;
    } else if (alignment === "center") {
        paddingClass = PADDING.PX_1;
    } else {// Right
        const columnIsSortable = smartTable.sortHelper.columnIsSortable(column.uid);
        paddingClass = columnIsSortable ? PADDING.PE_3 : PADDING.PE_1;// More end padding to display sort selector
    }

    return paddingClass;
}

/**
 * Returns display string value of the cell
 * @param cell Cell of the smart table to filter
 */
export function getDisplayStringValue<ItemData>(cell: SmartTableCell<ItemData>, smartTable: CjsSmartTable<ItemData>, renderCacheData: RenderCacheData | undefined): string | undefined {
    if (!cell.column) {
        getLogConsole().error("Column has not been set on given cell", cell);
        return undefined;
    }
    let cellDisplayString = cell.column.getDisplayString?.(cell, renderCacheData) ?? smartTable.cellPropertyConfigTypeHandler(cell);

    // Handle htmlStrings values if dev forgot to define getDisplayString
    if (cellDisplayString?.includes("<")) {// Don't parse all strings, only values contains some possible html code
        cellDisplayString = htmlStringToText(cellDisplayString);
    }
    return cellDisplayString;
}